import { useEffect, useMemo, useState } from "react";

import { type ChainListParams, useMarkets } from "@/api";
import { Combobox, type ComboboxOption } from "@/components/Combobox";

export interface FilterSidebarProps {
  initial: ChainListParams;
  onApply: (params: ChainListParams) => void;
}

interface FormState {
  k: string;
  origin_market: string;
  destination_market: string;
  max_deadhead_mi: string;
  min_slack_min: string;
  max_duration_days: string;
  risks: string;
  pickup_date: string;
  chain_end_date: string;
}

function paramsToForm(p: ChainListParams): FormState {
  return {
    k: p.k != null ? String(p.k) : "",
    origin_market: p.origin_market ?? "",
    destination_market: p.destination_market ?? "",
    max_deadhead_mi: p.max_deadhead_mi != null ? String(p.max_deadhead_mi) : "",
    min_slack_min: p.min_slack_min != null ? String(p.min_slack_min) : "",
    max_duration_days: p.max_duration_days != null ? String(p.max_duration_days) : "",
    risks: p.risks ?? "moderate,comfortable",
    pickup_date: p.pickup_date ?? "",
    chain_end_date: p.chain_end_date ?? "",
  };
}

function formToParams(f: FormState): ChainListParams {
  const numeric = (v: string) => (v ? Number(v) : undefined);
  // When both dates are set, the date span pins the duration exactly —
  // we drop `max_duration_days` so the backend isn't asked to filter on
  // a redundant (or contradictory) constraint. Matches the legacy
  // `syncDurationToDates` behavior (commit dc8120f).
  const bothDatesSet = f.pickup_date && f.chain_end_date;
  return {
    k: numeric(f.k) as number | undefined,
    origin_market: f.origin_market.trim() || undefined,
    destination_market: f.destination_market.trim() || undefined,
    max_deadhead_mi: numeric(f.max_deadhead_mi),
    min_slack_min: numeric(f.min_slack_min),
    max_duration_days: bothDatesSet ? undefined : numeric(f.max_duration_days),
    risks: f.risks || undefined,
    pickup_date: f.pickup_date || undefined,
    chain_end_date: f.chain_end_date || undefined,
  };
}

export function FilterSidebar({ initial, onApply }: FilterSidebarProps): JSX.Element {
  const [form, setForm] = useState<FormState>(() => paramsToForm(initial));
  const { data: marketsData } = useMarkets();

  // Re-sync when external params change (e.g. URL navigation).
  useEffect(() => {
    setForm(paramsToForm(initial));
  }, [initial]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Build the market dropdown options (prefix-filtered by what the user
  // types so combobox doesn't get unwieldy when we have ~100 markets).
  const allMarketOptions: ComboboxOption[] = useMemo(
    () =>
      (marketsData?.markets ?? []).map((m) => ({
        value: m.code,
        hint: `${m.chain_count} ${m.chain_count === 1 ? "chain" : "chains"}`,
      })),
    [marketsData],
  );

  const filterOptions = (input: string): ComboboxOption[] => {
    const q = input.trim().toLowerCase();
    if (!q) return allMarketOptions.slice(0, 25);
    return allMarketOptions
      .filter((o) => o.value.toLowerCase().includes(q))
      .slice(0, 25);
  };

  return (
    <form
      className="card p-4 space-y-3 sticky top-4"
      onSubmit={(e) => {
        e.preventDefault();
        onApply(formToParams(form));
      }}
    >
      <h2 className="font-semibold text-slate-900">Filters</h2>

      <div>
        <label className="label" htmlFor="filter-k">
          Chain length (k)
        </label>
        <select
          id="filter-k"
          className="input"
          value={form.k}
          onChange={(e) => update("k", e.target.value)}
        >
          <option value="">any</option>
          <option value="2">2-load</option>
          <option value="3">3-load</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Combobox
          id="filter-origin"
          label="Origin market"
          placeholder="IL_CHI"
          value={form.origin_market}
          onInputChange={(v) => update("origin_market", v)}
          onChange={(v) => update("origin_market", v)}
          options={filterOptions(form.origin_market)}
          emptyMessage="No matching markets"
        />
        <Combobox
          id="filter-dest"
          label="Destination market"
          placeholder="OH_CIN"
          value={form.destination_market}
          onInputChange={(v) => update("destination_market", v)}
          onChange={(v) => update("destination_market", v)}
          options={filterOptions(form.destination_market)}
          emptyMessage="No matching markets"
        />
      </div>

      <div>
        <label className="label" htmlFor="filter-deadhead">
          Max deadhead (mi)
        </label>
        <input
          id="filter-deadhead"
          type="number"
          inputMode="numeric"
          className="input"
          placeholder="200"
          value={form.max_deadhead_mi}
          onChange={(e) => update("max_deadhead_mi", e.target.value)}
        />
      </div>

      <div>
        <label className="label" htmlFor="filter-slack">
          Min slack (min)
        </label>
        <input
          id="filter-slack"
          type="number"
          inputMode="numeric"
          className="input"
          placeholder="60"
          value={form.min_slack_min}
          onChange={(e) => update("min_slack_min", e.target.value)}
        />
      </div>

      <div>
        <label className="label" htmlFor="filter-duration">
          Max duration (days)
        </label>
        <input
          id="filter-duration"
          type="number"
          inputMode="numeric"
          className="input"
          placeholder="3"
          value={form.max_duration_days}
          // When both date pickers are filled, the date span pins the
          // duration exactly so this input is redundant — disable it and
          // show the implied span as the placeholder. Matches the
          // legacy `syncDurationToDates` slider-lock behavior.
          disabled={Boolean(form.pickup_date && form.chain_end_date)}
          onChange={(e) => update("max_duration_days", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label" htmlFor="filter-pickup-date">
            Chain start
          </label>
          <input
            id="filter-pickup-date"
            type="date"
            className="input"
            value={form.pickup_date}
            onChange={(e) => update("pickup_date", e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="filter-chain-end-date">
            Chain end
          </label>
          <input
            id="filter-chain-end-date"
            type="date"
            className="input"
            value={form.chain_end_date}
            // Clamp `min` to `pickup_date` so the rep can't pick an end
            // before the start — same guard as legacy `endEl.min = start`.
            min={form.pickup_date || undefined}
            onChange={(e) => update("chain_end_date", e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="label" htmlFor="filter-risks">
          Risk tiers
        </label>
        <select
          id="filter-risks"
          className="input"
          value={form.risks}
          onChange={(e) => update("risks", e.target.value)}
        >
          <option value="moderate,comfortable">moderate + comfortable</option>
          <option value="comfortable">comfortable only</option>
          <option value="tight,moderate,comfortable">all</option>
        </select>
      </div>

      <div className="flex gap-2 pt-2">
        <button type="submit" className="btn-primary flex-1">
          Apply
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            setForm(paramsToForm({}));
            onApply({});
          }}
        >
          Reset
        </button>
      </div>
    </form>
  );
}
